import React, { useState, useEffect } from "react";
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from "chart.js";
import { Radar } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";

ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

export default function Nutrition({ dietReportData }) {

    const [nutritentData, setNutritentData] = useState([]);
    const [bodyCompositionData, setBodyCompositionData] = useState([]);
    const [intoleranceData, setIntoleranceData] = useState([]);
    const [foodRecommendData, setFoodRecommendData] = useState([]);
    const [foodAvoidData, setFoodAvoidData] = useState([]);
    const [selected, setSelected] = useState("Nutritents");
    const [active, setActive] = useState("div1");
    const [radarData, setRadarData] = useState([]);

    const buttons = ["Nutritents", "Intolerances", "Body Composition"];

    const riskFactorValues = {
        "UNDEFINED": 0,
        "LOW": 1,
        "NORMAL": 2,
        "MEDIUM-NORMAL": 3,
        "MEDIUM-HIGH": 4,
        "HIGH": 5,
    };

    useEffect(() => {
        if (dietReportData) {
            setNutritentData(dietReportData.nutrient || []);
            setBodyCompositionData(dietReportData.body_composition || []);
            setIntoleranceData(dietReportData.intolerances_and_deficiencies || []);
            setFoodRecommendData(transformFoodData(dietReportData.recommended_food));
            setFoodAvoidData(transformFoodData(dietReportData.to_avoid_food));
        }
    }, [dietReportData]);

    useEffect(() => {
        if (selected === 'Nutritents') {
            setRadarData(transformToRadar(nutritentData));
        } else if (selected === 'Intolerances') {
            setRadarData(transformToRadar(intoleranceData));
        } else if (selected === 'Body Composition') {
            setRadarData(transformToRadar(bodyCompositionData));
        }
    }, [selected, nutritentData, intoleranceData, bodyCompositionData]);

    const transformToRadar = (data) => {
        if (!Array.isArray(data) || data.length === 0) {
            return {
                labels: [],
                datasets: [],
            };
        }

        return {
            labels: data.map((item) => item.Condition || item.name),
            datasets: [
                {
                    label: "Levels",
                    data: data.map((item) => {
                        const rawValue = item["Risk factor"] || '';
                        const normalized = rawValue.trim().toUpperCase();
                        return riskFactorValues[normalized] ?? 0;
                    }),
                    backgroundColor: "rgba(142, 84, 233, 0.5)",
                    borderColor: "#8E54E9",
                    borderWidth: 2,
                    pointBackgroundColor: "rgba(0, 184, 217, 0.7)",
                    fill: true,
                },
            ],
        };
    };

    const transformFoodData = (foodObj) => {
        if (!foodObj || typeof foodObj !== 'object') return [];
        return Object.entries(foodObj).map(([category, sources]) => ({
            category,
            sources,
        }));
    };

    const options = {
        responsive: true,
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const index = context.dataIndex;
                        const dataset = context.chart.data.datasets[0];
                        const label = context.chart.data.labels[index];
                        const value = dataset.data[index];

                        const riskLabel = Object.keys(riskFactorValues).find(
                            (key) => riskFactorValues[key] === value
                        ) || "UNDEFINED";

                        return ` ${riskLabel}`;
                    },
                },
            },
            legend: {
                labels: {
                    color: "white",
                },
            },
        },
        scales: {
            r: {
                min: 0,
                max: 5,
                ticks: {
                    stepSize: 1,
                    color: "white",
                    backdropColor: "transparent",
                    beginAtZero: true,
                    callback: (value) => value.toString(),
                },
                angleLines: { color: "#C3CAD0" },
                grid: { color: "#C3CAD0" },
                pointLabels: {
                    color: "white",
                    font: { size: 11 },
                    callback: function (label) {
                        const words = label.split(" ");
                        const maxWordsPerLine = 2;
                        const lines = [];
                        for (let i = 0; i < words.length; i += maxWordsPerLine) {
                            lines.push(words.slice(i, i + maxWordsPerLine).join(" "));
                        }
                        return lines;
                    },
                },
            },
        },
    };

    const handleTab = (tab) => {
        setActive(tab)
    }

    return (
        <main className="w-full">
            <div className="flex md:flex-row flex-col-reverse justify-between lg:justify-center items-center px-5 gap-5 bg-white/5 rounded-xl ">
                <div>
                    <div className="mb-3">
                        <div className="flex md:flex-row flex-col items-center gap-3">
                            <h1
                                onClick={() => handleTab('div1')}
                                className={` cursor-pointer text-xl sm:text-2xl border-2 py-2 px-5 ${active === 'div1' ? 'border-[#A588FF]' : 'border-transparent'}`}
                            >
                                Nutrition Report{" "}
                            </h1>
                            <p className=" sm:text-3xl text-white md:rotate-0 rotate-90 ">
                                {" "}
                                |
                            </p>
                            <p onClick={() => handleTab('div2')}
                                className={`cursor-pointer text-xl sm:text-2xl border-2 py-2 px-5 ${active === 'div2' ? 'border-[#A588FF]' : 'border-transparent'}`}
                            >
                                Food Suggestion
                            </p>
                        </div>
                    </div>
                    <div className=" flex justify-center mt-2 mb-5 ">
                        <p className="text-white/75 font-light">
                            Based on the latest analysis of your performance and nutrition
                            reports, we've identified some key insights that could
                            significantly enhance your game.
                        </p>
                    </div>
                </div>
                <div>
                    <img src="/assets/others/displayplayer.svg"></img>
                </div>
            </div>
            {active === 'div1' && (
                (nutritentData.length === 0 && intoleranceData.length === 0 && bodyCompositionData.length === 0)
                    ? (
                        <div className="flex flex-col items-center justify-center h-[400px] md:h-[500px] w-full bg-black/40 backdrop-blur-sm rounded-lg text-white text-center">
                            <h2 className="text-2xl font-bold mb-2">Nutrition Report Unavailable</h2>
                            <p className="text-sm text-white/70">Your nutrition data hasn't been uploaded yet. Please check back later.</p>
                        </div>
                    )
                    : (
                        <div>

                            <div className="bg-[#2F1073] py-3 px-10 mb-6">
                                <div className="flex flex-wrap justify-around gap-2 z-40">
                                    {buttons.map((label) => (
                                        <button
                                            key={label}
                                            onClick={() => setSelected(label)}
                                            className={`py-1 px-7 text-sm shadow-md z-40 cursor-pointer transition-all flex items-center gap-2
                                        ${selected === label
                                                    ? "bg-[#A588FF] text-white shadow-[#4A3AFF]/50"
                                                    : "bg-white text-black border border-[#A588FF] shadow-none"
                                                }`}
                                            style={{ clipPath: "polygon(90% 0, 100% 50%, 100% 100%, 0 100%, 0 0)" }}
                                        >
                                            {label}
                                            <span><i className="fa-solid fa-arrow-right"></i></span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Left - Radar Chart */}
                                <div className="bg-gradient-to-b from-white/20 to-white/0 p-[1px] rounded-xl">
                                    <div className="bg-[#0b0a12] p-6 rounded-xl h-full relative">
                                        <h2 className="text-xl font-bold mb-2 text-center">Your Core Vitamin Levels</h2>
                                        <p className="text-white/50 text-sm text-center w-[90%] md:w-[70%] mx-auto font-light">
                                            Vital nutrients driving your energy, recovery, and fitness.
                                        </p>

                                        <div className="w-full z-20 mt-4 h-96 mx-auto flex justify-center items-center">
                                            {radarData?.datasets?.length > 0 ? (
                                                <Radar data={radarData} options={options} />
                                            ) : (
                                                <p className="text-center text-sm text-white/50">No data available</p>
                                            )}
                                        </div>

                                        {/* <div className="flex justify-center items-center z-20">
                                                    <div className="cursor-pointer bg-transparent border-2 border-white rounded-full px-5 py-2 mt-4 flex justify-center items-center gap-3 mx-auto z-20">
                                                        <button className="text-sm z-20">View Full Report</button>
                                                        <p className="z-20 bg-white rounded-full w-7 h-7 flex items-center justify-center">
                                                            <i className="fa-solid fa-arrow-right" style={{ color: "#5C1FE2" }}></i>
                                                        </p>
                                                    </div>
                                                </div> */}
                                    </div>
                                </div>

                                {/* Right - Conditions with Recommendations */}
                                <div className="bg-gradient-to-b from-white/20 to-white/0 p-[1px] rounded-xl">
                                    <div className="bg-[#0b0a12] p-3 sm:p-6 rounded-xl h-full relative">
                                        <h2 className="text-lg font-semibold mb-4 text-center">Condition-wise Recommendations</h2>
                                        <ul className="space-y-4 mt-3 h-[500px] overflow-y-auto z-20">
                                            {(selected === "Nutritents" ? nutritentData :
                                                selected === "Intolerances" ? intoleranceData :
                                                    selected === "Body Composition" ? bodyCompositionData : []).map((item, idx) => (
                                                        <li key={idx}>
                                                            <div className="bg-gradient-to-r from-white/10 to-white/0 p-[1px] rounded-lg">
                                                                <div className="bg-black/35 p-3 rounded-lg h-full relative">
                                                                    <h3 className="font-semibold text-sm mb-1 text-white/90">
                                                                        {item.Condition || item.name}
                                                                    </h3>
                                                                    <p className="text-xs text-white/60">
                                                                        Risk Factor: <span className="font-medium text-white">{item["Risk factor"] || "N/A"}</span>
                                                                    </p>
                                                                    {item.Recommendations && (
                                                                        <p className="text-xs mt-1 text-white/60">
                                                                            Recommendation: <span className="text-white/80">{item.Recommendations}</span>
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
            )}
            {active === 'div2' && (
                (foodRecommendData.length === 0 && foodAvoidData.length === 0)
                    ? (
                        <div className="flex flex-col items-center justify-center h-[400px] md:h-[500px] w-full bg-black/40 backdrop-blur-sm rounded-lg text-white text-center">
                            <h2 className="text-2xl font-bold mb-2">Food Suggestions Unavailable</h2>
                            <p className="text-sm text-white/70">Your food recommendation data is not available yet.  Please check back later.</p>
                        </div>
                    )
                    : (
                        <div>
                            <div className=" bg-[#2F1073] py-3 px-5 md:px-10 mb-6">
                                <div className=" z-40 ">
                                    <p className=" font-semibold ">
                                        Here’s a quick breakdown of what to eat and what to avoid to
                                        stay at your peak.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className=" bg-gradient-to-b from-white/20 to-white/0 p-[1px] rounded-xl ">
                                    <div className="bg-[#0b0a12] p-3 sm:p-6 rounded-xl h-full relative">
                                        <div className="w-full flex justify-end absolute right-0 bottom-0 rounded-xl overflow-hidden z-0">
                                            <svg
                                                width="597"
                                                height="703"
                                                viewBox="0 0 597 703"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g opacity="0.3" filter="url(#filter0_f_844_1047)">
                                                    <path
                                                        d="M369 199L551.333 625.645L997.734 947.5L369 842.71L-259.734 947.5L186.667 625.645L369 199Z"
                                                        fill="url(#paint0_linear_844_1047)"
                                                    />
                                                </g>
                                                <defs>
                                                    <filter
                                                        id="filter0_f_844_1047"
                                                        x="-659.734"
                                                        y="-201"
                                                        width="2057.47"
                                                        height="1548.5"
                                                        filterUnits="userSpaceOnUse"
                                                        colorInterpolationFilters="sRGB"
                                                    >
                                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                        <feBlend
                                                            mode="normal"
                                                            in="SourceGraphic"
                                                            in2="BackgroundImageFix"
                                                            result="shape"
                                                        />
                                                        <feGaussianBlur
                                                            stdDeviation="200"
                                                            result="effect1_foregroundBlur_844_1047"
                                                        />
                                                    </filter>
                                                    <linearGradient
                                                        id="paint0_linear_844_1047"
                                                        x1="-914.356"
                                                        y1="151.41"
                                                        x2="1080.22"
                                                        y2="1202.97"
                                                        gradientUnits="userSpaceOnUse"
                                                    >
                                                        <stop stopColor="#2968FF" />
                                                        <stop offset="1" stopColor="#739DFF" />
                                                    </linearGradient>
                                                </defs>
                                            </svg>
                                        </div>

                                        <h2 className="text-lg font-semibold mb-4 text-center ">
                                            Top Foods for Performance
                                        </h2>
                                        <ul className="space-y-4 mt-3 h-[500px] overflow-y-auto z-10 relative">
                                            {foodRecommendData.map((group, idx) => (
                                                <li key={idx} className="mb-4">
                                                    <div className="bg-gradient-to-r from-white/10 to-white/0 p-[1px] rounded-lg">
                                                        <div className="bg-black/35 p-3 rounded-lg">
                                                            <h3 className="font-bold text-sm mb-2">{group.category === "Carbohydrates" ? "Carbohydrates, Refined and Whole" : group.category}</h3>
                                                            <ul className="list-disc list-inside text-sm space-y-1">
                                                                {group.sources.map((item, i) => (
                                                                    <li key={i}>{item}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <div className=" bg-gradient-to-b from-white/20 to-white/0 p-[1px] rounded-xl ">
                                    <div className="bg-[#0b0a12] p-3 sm:p-6 rounded-xl h-full relative">
                                        <div className="w-full flex justify-end absolute right-0 bottom-0 rounded-xl overflow-hidden z-0">
                                            <svg
                                                width="597"
                                                height="703"
                                                viewBox="0 0 597 703"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g opacity="0.3" filter="url(#filter0_f_844_1047)">
                                                    <path
                                                        d="M369 199L551.333 625.645L997.734 947.5L369 842.71L-259.734 947.5L186.667 625.645L369 199Z"
                                                        fill="url(#paint0_linear_844_1047)"
                                                    />
                                                </g>
                                                <defs>
                                                    <filter
                                                        id="filter0_f_844_1047"
                                                        x="-659.734"
                                                        y="-201"
                                                        width="2057.47"
                                                        height="1548.5"
                                                        filterUnits="userSpaceOnUse"
                                                        colorInterpolationFilters="sRGB"
                                                    >
                                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                                        <feBlend
                                                            mode="normal"
                                                            in="SourceGraphic"
                                                            in2="BackgroundImageFix"
                                                            result="shape"
                                                        />
                                                        <feGaussianBlur
                                                            stdDeviation="200"
                                                            result="effect1_foregroundBlur_844_1047"
                                                        />
                                                    </filter>
                                                    <linearGradient
                                                        id="paint0_linear_844_1047"
                                                        x1="-914.356"
                                                        y1="151.41"
                                                        x2="1080.22"
                                                        y2="1202.97"
                                                        gradientUnits="userSpaceOnUse"
                                                    >
                                                        <stop stopColor="#2968FF" />
                                                        <stop offset="1" stopColor="#739DFF" />
                                                    </linearGradient>
                                                </defs>
                                            </svg>
                                        </div>

                                        <h2 className="text-lg font-semibold mb-4 text-center ">
                                            Avoid These Game Killers
                                        </h2>
                                        <ul className="space-y-4 mt-3 h-[500px] overflow-y-auto z-10 relative">
                                            {foodAvoidData.map((group, idx) => (
                                                <li key={idx} className="mb-4">
                                                    <div className="bg-gradient-to-r from-white/10 to-white/0 p-[1px] rounded-lg">
                                                        <div className="bg-black/35 p-3 rounded-lg">
                                                            <h3 className="font-bold text-sm mb-2">{group.category === "Carbohydrates" ? "Carbohydrates, Refined and Whole" : group.category}</h3>
                                                            <ul className="list-disc list-inside text-sm space-y-1">
                                                                {group.sources.map((item, i) => (
                                                                    <li key={i}>{item}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )
            )}
        </main>
    )
}
